/**
 * Element description + matched-CSS collection.
 *
 * Ported from `devdeck/ui/public/devdeck-inspector.js` (`describe`,
 * `matchesOrContains`, `collectCss`). Caps are smaller here — findings are
 * stored in a database, not pasted into a terminal.
 */

import type { ElementContext } from "../shared/schemas";

const HTML_CAP = 50_000;
const CSS_CAP = 50_000;

export function describe(el: Element): string {
  let s = el.tagName.toLowerCase();
  if (el.id) s += `#${el.id}`;
  const classes =
    typeof el.className === "string"
      ? el.className.split(/\s+/).filter(Boolean)
      : [];
  if (classes.length)
    s += "." + classes.slice(0, 4).join(".") + (classes.length > 4 ? "…" : "");
  const ref = el.getAttribute("data-ref");
  if (ref) s += ` [data-ref="${ref}"]`;
  return s;
}

// Pseudo-classes/elements are stripped for a second match attempt so
// state-dependent rules are included even when the element isn't in that
// state. Structural ones (:not/:is/:where/:has) are kept — removing them would
// change what the selector means. (?<!\\) skips escaped colons in
// Tailwind-style class names like `.hover\:x`.
const PSEUDO_RE =
  /(?<!\\)::?(?!not\b|is\b|where\b|has\b)[a-zA-Z-]+(\((?:[^()]|\([^()]*\))*\))?/g;

function matchesOrContains(el: Element, sel: string): boolean {
  try {
    if (el.matches(sel)) return true;
  } catch {
    /* invalid selector for this engine */
  }
  try {
    if (el.querySelector(sel)) return true;
  } catch {
    /* ignore */
  }
  const bare = sel.replace(PSEUDO_RE, "");
  if (bare && bare !== sel) {
    try {
      if (el.matches(bare)) return true;
    } catch {
      /* ignore */
    }
    try {
      if (el.querySelector(bare)) return true;
    } catch {
      /* ignore */
    }
  }
  return false;
}

const indent = (text: string) =>
  text
    .split("\n")
    .map((l) => "  " + l)
    .join("\n");

export function collectCss(el: Element): string {
  const out: string[] = [];
  const seen = new Set<string>();
  let size = 0;
  let unreadable = 0;
  let truncated = false;

  const walk = (rules: CSSRuleList, wrappers: string[]) => {
    for (const rule of Array.from(rules)) {
      if (truncated) return;
      if (rule instanceof CSSStyleRule) {
        if (!matchesOrContains(el, rule.selectorText)) continue;
        let text = rule.cssText;
        for (let i = wrappers.length - 1; i >= 0; i--)
          text = `${wrappers[i]} {\n${indent(text)}\n}`;
        if (seen.has(text)) continue;
        seen.add(text);
        size += text.length;
        if (size > CSS_CAP) {
          truncated = true;
          return;
        }
        out.push(text);
      } else {
        const grouping = rule as CSSGroupingRule;
        if (grouping.cssRules && grouping.cssRules.length) {
          // @media / @supports / @layer / @container — keep the prelude as a
          // wrapper so matched rules stay in context.
          const head = rule.cssText.slice(0, rule.cssText.indexOf("{")).trim();
          walk(grouping.cssRules, head ? [...wrappers, head] : wrappers);
        }
      }
    }
  };

  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      unreadable++; // cross-origin sheet (e.g. a font CDN) — can't be read
      continue;
    }
    walk(rules, []);
  }

  const notes: string[] = [];
  if (truncated) notes.push(`/* truncated at ${CSS_CAP} chars */`);
  if (unreadable)
    notes.push(`/* ${unreadable} cross-origin stylesheet(s) skipped */`);
  if (!out.length) notes.push("/* no stylesheet rules matched this element */");
  return [...out, ...notes].join("\n\n");
}

export function captureElementContext(el: Element): ElementContext {
  let outerHTML = el.outerHTML;
  if (outerHTML.length > HTML_CAP)
    outerHTML = outerHTML.slice(0, HTML_CAP) + "\n<!-- truncated -->";
  return { describe: describe(el), outerHTML, matchedCss: collectCss(el) };
}
